import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSEO } from '../../utils/seo';

export default function DiagnosiPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: 'Diagnosi Elettronica Auto e Moto a Brescia - Scanner OBD2 | Metamorfosi Preparazioni',
    description: 'Diagnosi elettronica completa per auto e moto a domicilio a Brescia e provincia. Scanner OBD2 professionale per lettura codici errore, monitoraggio dati in tempo reale, reset spie e configurazione. Identifichiamo i problemi rapidamente a Brescia.',
    keywords: 'diagnosi elettronica Brescia, scanner OBD2 Brescia, codici errore Brescia, diagnosi auto Brescia, diagnosi moto Brescia, centralina Brescia, check engine Brescia, spia motore Brescia, meccanico diagnosi Brescia',
    canonical: '/diagnosi',
    ogTitle: 'Diagnosi Elettronica a Brescia - Scanner OBD2 Professionale',
    ogDescription: 'Diagnosi completa con strumenti professionali a Brescia',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Diagnosi Elettronica Veicoli a Brescia',
      provider: {
        '@type': 'AutoRepair',
        name: 'Metamorfosi Preparazioni',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
        telephone: '+39-351-831-2394',
        email: 'metamorfosi.preparazioni@gmail.com'
      },
      areaServed: {
        '@type': 'City',
        name: 'Brescia',
        containedIn: {
          '@type': 'AdministrativeArea',
          name: 'Provincia di Brescia'
        }
      },
      description: 'Diagnosi elettronica professionale per auto e moto a Brescia con scanner OBD2. Lettura codici errore, monitoraggio parametri in tempo reale, reset spie di servizio e configurazione componenti.',
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceRange: '$'
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black shadow-lg">
        <div className="w-full px-4 sm:px-6 lg:px-12">
          <div className="flex items-center justify-between h-16 sm:h-18 md:h-20">
            <Link to="/" className="flex-shrink-0">
              <img 
                src="https://static.readdy.ai/image/26c3401126eecf15eb2286ded28883ff/289e4af9c1f47a64ff4db8c7e8c98e4a.jpeg" 
                alt="Metamorfosi Preparazioni Logo" 
                className="h-10 sm:h-12 md:h-14 w-auto"
              />
            </Link>
            <Link 
              to="/" 
              className="flex items-center text-white hover:text-[#E31E24] transition-colors duration-300 cursor-pointer"
            >
              <i className="ri-arrow-left-line mr-2"></i>
              <span className="font-semibold text-sm">Torna alla Home</span>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-gray-100 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="inline-block mb-6">
              <span className="inline-block px-6 py-2 border-2 border-[#E31E24] text-[#E31E24] bg-white font-bold text-xs tracking-widest rounded-full uppercase">
                Diagnosi Professionale
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-black mb-6">
              Diagnosi Elettronica<br />del Veicolo
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Utilizziamo strumenti diagnostici professionali per identificare rapidamente qualsiasi problema del tuo veicolo, risparmiando tempo e denaro.
            </p>
          </div>
        </div>
      </section>

      {/* Tester Section */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl md:rounded-3xl overflow-hidden shadow-xl md:shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/78a92fc1dfbfd2671a1ae501e3831929.jpg"
                  alt="Tester Diagnostico Professionale"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            <div>
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-black mb-4 md:mb-6">
                Tester Diagnostico Professionale
              </h2>
              <p className="text-gray-600 text-sm sm:text-base md:text-lg mb-6 md:mb-8">
                Il nostro scanner OBD2 professionale è uno strumento avanzato che ci permette di comunicare direttamente con la centralina del tuo veicolo per identificare problemi e malfunzionamenti.
              </p>

              <div className="space-y-5 md:space-y-6">
                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#E31E24] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-file-list-3-line text-white text-lg md:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-black text-base md:text-lg mb-1 md:mb-2">Lettura Codici Errore</h3>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      Identifichiamo immediatamente i codici di errore memorizzati dalla centralina, permettendoci di capire esattamente quale componente necessita attenzione.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#E31E24] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-chart-line text-white text-lg md:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-black text-base md:text-lg mb-1 md:mb-2">Monitoraggio Dati in Tempo Reale</h3>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      Visualizziamo parametri come temperatura motore, pressione turbo, lambda, giri motore e molto altro mentre il veicolo è in funzione.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3 md:gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 bg-[#E31E24] rounded-xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-settings-3-line text-white text-lg md:text-xl"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-black text-base md:text-lg mb-1 md:mb-2">Reset e Configurazione</h3>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      Possiamo resettare spie di servizio, rigenerare il filtro antiparticolato (DPF) e configurare nuovi componenti dopo la sostituzione.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cosa Diagnostichiamo */}
      <section className="py-12 md:py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-black mb-3 md:mb-4">
              Cosa Possiamo Diagnosticare
            </h2>
            <p className="text-gray-600 text-sm sm:text-base md:text-lg max-w-2xl mx-auto">
              Il nostro strumento diagnostico copre tutti i principali sistemi del veicolo
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {[
              { icon: 'ri-settings-5-line', title: 'Sistema Motore', desc: 'Iniezione, accensione, sensori, turbo, scarico' },
              { icon: 'ri-steering-2-line', title: 'ABS e ESP', desc: 'Sistema frenante antibloccaggio e controllo stabilità' },
              { icon: 'ri-shield-check-line', title: 'Airbag', desc: 'Sistema di sicurezza passiva e sensori di impatto' },
              { icon: 'ri-temp-cold-line', title: 'Climatizzazione', desc: 'Aria condizionata e sistema di riscaldamento' },
              { icon: 'ri-flashlight-line', title: 'Sistema Elettrico', desc: 'Batteria, alternatore, luci e centraline' },
              { icon: 'ri-leaf-line', title: 'Emissioni', desc: 'Catalizzatore, sonda lambda, filtro antiparticolato' }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-xl md:rounded-2xl p-5 md:p-6 shadow-md md:shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-11 h-11 md:w-14 md:h-14 bg-[#E31E24] rounded-lg md:rounded-xl flex items-center justify-center mb-3 md:mb-4">
                  <i className={`${item.icon} text-white text-xl md:text-2xl`}></i>
                </div>
                <h3 className="font-bold text-black text-base md:text-lg mb-1 md:mb-2">{item.title}</h3>
                <p className="text-gray-600 text-xs sm:text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-24 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
            Prenota la Tua Diagnosi
          </h2>
          <p className="text-sm sm:text-base md:text-lg text-white/80 mb-6 md:mb-8 max-w-2xl mx-auto leading-relaxed">
            Contattaci per una diagnosi completa del tuo veicolo. Identifichiamo i problemi prima che diventino costosi.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4">
            <button 
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const element = document.getElementById('contatti');
                  if (element) {
                    const offset = 80;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - offset;
                    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                  }
                }, 300);
              }}
              className="inline-flex items-center bg-[#E31E24] text-white font-bold text-sm px-8 py-4 rounded-full hover:bg-[#c41a1f] transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <span className="mr-3">RICHIEDI PREVENTIVO</span>
              <i className="ri-arrow-right-line text-lg"></i>
            </button>
            <a 
              href="https://wa.me/393518312394"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-white text-black font-bold text-sm px-8 py-4 rounded-full hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-[#25D366] text-xl mr-3"></i>
              <span>WHATSAPP</span>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#E31E24] py-6 md:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="flex flex-col items-center space-y-4">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/26c3401126eecf15eb2286ded28883ff/289e4af9c1f47a64ff4db8c7e8c98e4a.jpeg" 
                alt="Metamorfosi Preparazioni Logo" 
                className="h-10 w-auto"
              />
            </Link>
            <div className="flex items-center">
              <i className="ri-mail-line text-white text-lg mr-2"></i>
              <a href="mailto:metamorfosi.preparazioni@gmail.com" className="text-white/90 hover:text-white transition-colors duration-300 cursor-pointer text-xs sm:text-sm break-all">
                metamorfosi.preparazioni@gmail.com
              </a>
            </div>
            <p className="text-white/80 text-xs sm:text-sm text-center">
              © 2025 Metamorfosi Preparazioni. Tutti i diritti riservati.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-white/70 hover:text-white text-xs sm:text-sm transition-colors duration-300 cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
